import { numberToCurrency } from '@/utils'
import { monthLabel } from '../model/months'
import type { AllAccountsAsOf, MonthTotal } from '../model/portfolioOverview'
import type { AccountMonth } from '../model/types'
import {
  cashSourceNote,
  countOf,
  joinWithAnd,
  longDate,
  securitiesSourceNote,
} from '../portfolioText'

const MAX_NOTES = 3

const notesFor = (
  accountMonths: AccountMonth[],
  sentence: (entry: AccountMonth) => string,
) => accountMonths.map(sentence)

// Why the hero says "No total", one sentence per account that blocks it.
export const noTotalNotes = (total: MonthTotal): string[] => {
  const notes = [
    ...notesFor(
      total.missing,
      (entry) =>
        `${entry.accountName} has no ${monthLabel(entry.month)} statement.`,
    ),
    ...notesFor(
      total.verifying,
      (entry) =>
        `${entry.accountName}'s ${monthLabel(entry.month)} statement is still being verified.`,
    ),
    ...notesFor(
      total.midCycle,
      (entry) =>
        `${entry.accountName} has no statement that ends in ${monthLabel(entry.month)}.`,
    ),
    ...total.unknownValue.map((entry) =>
      entry.readWithoutAccount
        ? `The ${monthLabel(entry.month)} statement was read without ${entry.accountName}.`
        : `${entry.accountName}'s ${monthLabel(entry.month)} statement ${entry.failedRead ? 'could not be read' : 'gives no value'}.`,
    ),
    ...notesFor(
      total.twoStatements,
      (entry) =>
        `${entry.accountName} has two statements for ${monthLabel(entry.month)}.`,
    ),
  ]
  if (notes.length <= MAX_NOTES) return notes
  const more = notes.length - MAX_NOTES
  return [...notes.slice(0, MAX_NOTES), `And ${countOf(more, 'more account')}.`]
}

const isSingleAccount = (asOf: AllAccountsAsOf) => asOf.accountCount === 1

const statementsWithoutCash = (asOf: AllAccountsAsOf) =>
  asOf.accounts.filter(
    (account) => account.status === 'statement' && account.cash == null,
  )

// Cash shows when any statement on the as-of date printed a balance. One with none leaves
// the others' cash in the total, so the labels still say cash.
export const isCashShown = (asOf: AllAccountsAsOf): boolean =>
  asOf.accounts.some(
    (account) => account.status === 'statement' && account.cash != null,
  )

// Without the reconcile feature lukita withholds every cash balance, so the plan is the reason.
export const cashLeftOutNote = (
  asOf: AllAccountsAsOf,
  isCashInPlan: boolean,
): string | null => {
  if (!isCashInPlan) return 'Cash balances are not part of this plan.'
  const names = statementsWithoutCash(asOf).map((account) => account.name)
  if (names.length === 0) return null
  return `Cash missing for ${joinWithAnd(names)}.`
}

export const totalLabel = (asOf: AllAccountsAsOf): string => {
  const date = longDate(asOf.asOfDate)
  const hasCash = isCashShown(asOf)
  if (isSingleAccount(asOf)) {
    return hasCash ? `Securities and cash on ${date}` : `Securities on ${date}`
  }
  const accounts = countOf(asOf.accountCount, 'account')
  return hasCash
    ? `Total of ${accounts} on ${date}`
    : `Securities in ${accounts} on ${date}`
}

const accountsWithCash = (asOf: AllAccountsAsOf) =>
  asOf.accounts.filter((account) => account.cash != null).length

// "Cash in 2 of 3 accounts" when a statement gives no cash figure, so the label never claims all.
export const partLabel = (
  asOf: AllAccountsAsOf,
  part: 'Securities' | 'Cash',
): string => {
  if (isSingleAccount(asOf)) return part
  const accounts = countOf(asOf.accountCount, 'account')
  const withCash = accountsWithCash(asOf)
  return part === 'Cash' && withCash < asOf.accountCount
    ? `Cash in ${withCash} of ${accounts}`
    : `${part} in ${accounts}`
}

// Words beside a figure that say where it came from. One account reads its own figures.
export const securitiesNote = (asOf: AllAccountsAsOf): string | null =>
  isSingleAccount(asOf) && asOf.accounts[0]
    ? securitiesSourceNote(asOf.accounts[0])
    : null

export const cashNote = (asOf: AllAccountsAsOf): string | null => {
  if (isSingleAccount(asOf) && asOf.accounts[0]) {
    return cashSourceNote(asOf.accounts[0])
  }
  const negative = asOf.accounts.filter((account) => (account.cash ?? 0) < 0)
  if (negative.length === 0) return null
  if (negative.length === 1) {
    return `includes ${numberToCurrency(negative[0].cash ?? 0)} in ${negative[0].name}`
  }
  return `includes margin debits in ${countOf(negative.length, 'account')}`
}

export const changeLabel = (asOf: AllAccountsAsOf): string | null => {
  if (!asOf.change) return null
  const since = longDate(asOf.change.since)
  return asOf.change.accountCount < asOf.accountCount
    ? `Change in the ${countOf(asOf.change.accountCount, 'account')} since ${since}`
    : `Change since ${since}`
}

export const chartLabel = (asOf: AllAccountsAsOf): string =>
  isSingleAccount(asOf)
    ? 'Securities and cash at each month end'
    : `Total of the ${countOf(asOf.accountCount, 'account')} at each month end`
